import { apiFetch, readApiJson } from '@/lib/api-fetch'
import type { InventoryFilters } from '@/lib/api/client/inventory'
import {
  PublicationsSchema,
  StorefrontBatchApplySchema,
  StorefrontBatchPreviewSchema,
  StorefrontBatchStatusSchema,
  StorefrontSettingsSchema,
  IdentifierExampleSchema,
  MediaRepairApplySchema,
  MediaRepairPreviewSchema,
  type SellerArticlePolicy,
  type StorefrontBatchPreview,
  type StorefrontBatchStatus,
  type StorefrontSettings,
  type StorefrontTarget,
  type Publication,
  type MediaRepairPreview,
  type MediaRepairApply,
} from '@/lib/api/schemas/storefronts.schema'

export async function getStorefrontSettings(signal?: AbortSignal): Promise<StorefrontSettings> {
  return StorefrontSettingsSchema.parse(await readApiJson(await apiFetch('/api/settings/storefronts', { signal })))
}

// The update contract forbids unknown fields, and a target read back from the
// server also carries its credential status and environment, which are not
// settable. Each target is rebuilt from the two fields the server accepts
// rather than spread, so a new read-only field cannot turn a save into a 422.
export async function updateStorefrontSettings(settings: StorefrontSettings): Promise<StorefrontSettings> {
  const targets: Record<string, { sellerArticlePolicy: SellerArticlePolicy; cardPublishingEnabled: boolean }> = {}
  for (const [target, value] of Object.entries(settings.targets)) {
    targets[target] = { sellerArticlePolicy: value.sellerArticlePolicy, cardPublishingEnabled: value.cardPublishingEnabled }
  }
  const response = await apiFetch('/api/settings/storefronts', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sellerArticlePolicy: settings.sellerArticlePolicy,
      ymarketCardPublishingEnabled: settings.ymarketCardPublishingEnabled,
      wildberriesCardPublishingEnabled: settings.wildberriesCardPublishingEnabled,
      ozonCardPublishingEnabled: settings.ozonCardPublishingEnabled,
      oriSiteCardPublishingEnabled: settings.oriSiteCardPublishingEnabled,
      ymarketPricingPolicy: settings.ymarketPricingPolicy,
      targets,
    }),
  })
  return StorefrontSettingsSchema.parse(await readApiJson(response))
}

export async function getStorefrontPublications(signal?: AbortSignal): Promise<Publication[]> {
  const query = new URLSearchParams({ publishedOnly: 'true' })
  const response = await apiFetch(`/api/storefront-publishing/publications?${query.toString()}`, { signal })
  return PublicationsSchema.parse(await readApiJson(response)).items
}

export type StorefrontBatchFilter = Pick<
  InventoryFilters,
  'search' | 'localStock' | 'availableOn' | 'marketplacePresence' | 'absentOn' | 'withPhotos' | 'problems' | 'missingMarking' | 'unresolvedOnly'
>

export type StorefrontBatchSelection =
  | { kind: 'ids'; ids: readonly string[] }
  | { kind: 'filter'; filter: InventoryFilters; expectedTotal: number }

function batchFilterOf(filters: InventoryFilters): StorefrontBatchFilter {
  return {
    search: filters.search,
    localStock: filters.localStock ?? 'in_stock',
    availableOn: filters.availableOn ?? [],
    marketplacePresence: filters.marketplacePresence,
    absentOn: filters.absentOn ?? [],
    withPhotos: filters.withPhotos ?? false,
    problems: filters.problems ?? false,
    missingMarking: filters.missingMarking ?? false,
    unresolvedOnly: filters.unresolvedOnly ?? false,
  }
}

export async function previewStorefrontBatch(selection: StorefrontBatchSelection, targets: readonly StorefrontTarget[], signal?: AbortSignal): Promise<StorefrontBatchPreview> {
  const body = selection.kind === 'ids'
    ? { productIds: selection.ids, targets }
    : { filter: batchFilterOf(selection.filter), expectedTotal: selection.expectedTotal, targets }
  const response = await apiFetch('/api/storefront-publishing/batches/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  })
  return StorefrontBatchPreviewSchema.parse(await readApiJson(response))
}

export type IdentifierExample = {
  identifier: string | null
  source: string | null
  refusal: { code: string; label: string } | null
}

export async function getIdentifierExample(target: StorefrontTarget, productId: string, signal?: AbortSignal): Promise<IdentifierExample> {
  const query = new URLSearchParams({ target, productId })
  const response = await apiFetch(`/api/storefront-publishing/identifier-example?${query.toString()}`, { signal })
  return IdentifierExampleSchema.parse(await readApiJson(response))
}

export async function applyStorefrontBatch(batchHash: string): Promise<string> {
  const response = await apiFetch('/api/storefront-publishing/batches/apply', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ batchHash }),
  })
  return StorefrontBatchApplySchema.parse(await readApiJson(response)).batchId
}

export async function getStorefrontBatchStatus(batchId: string, signal?: AbortSignal): Promise<StorefrontBatchStatus> {
  const response = await apiFetch(`/api/storefront-publishing/batches/${encodeURIComponent(batchId)}`, { signal })
  return StorefrontBatchStatusSchema.parse(await readApiJson(response))
}

export async function resumeStorefrontBatch(batchId: string): Promise<string> {
  const response = await apiFetch(`/api/storefront-publishing/batches/${encodeURIComponent(batchId)}/resume`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })
  return StorefrontBatchApplySchema.parse(await readApiJson(response)).batchId
}

export async function previewStorefrontMediaRepair(input: {
  productId: string
  providerAccountId: string
  providerId?: string | null
  sellerArticle: string
  mediaLocators?: readonly string[]
  predecessorBatchId?: string | null
}): Promise<MediaRepairPreview> {
  const response = await apiFetch('/api/storefront-publishing/media-repairs/preview', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      productId: input.productId,
      providerAccountId: input.providerAccountId,
      providerId: input.providerId ?? null,
      sellerArticle: input.sellerArticle,
      mediaLocators: input.mediaLocators ?? [],
      predecessorBatchId: input.predecessorBatchId ?? null,
    }),
  })
  return MediaRepairPreviewSchema.parse(await readApiJson(response))
}

export async function applyStorefrontMediaRepair(previewHash: string): Promise<MediaRepairApply> {
  const response = await apiFetch('/api/storefront-publishing/media-repairs/apply', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ previewHash }),
  })
  return MediaRepairApplySchema.parse(await readApiJson(response))
}
